<template>
  <div class="dashboard">
    <SideNav v-bind:page_active="page_active" />

    <div class="main-section">
      <TopNav />

      <div class="content-body">
        <div class="row breadcrumb-area">
          <div class="col-lg-6">
            
          </div>
          <div class="col-lg-6">
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item"><a href="/dashboard">Home</a></li>
                <li class="breadcrumb-item"><a href="/settings">Settings</a></li>
                <li class="breadcrumb-item active" aria-current="page">Sessions</li>
              </ol>
            </nav>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-12">
            <div class="sessions-card">
              <div class="sessions-card-header">
                <div>
                  <h6 class="sessions-title">Logged-in Devices</h6>
                  <p class="sessions-subtitle">
                    These devices have recently accessed your account.
                    <span v-if="meta.total" class="total-badge">
                      {{ meta.total }} device{{ meta.total !== 1 ? 's' : '' }}
                    </span>
                  </p>
                </div>
                <button class="btn-refresh" @click="listSessions(meta.current_page)" :disabled="loading">
                  <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
                  Refresh
                </button>
              </div>

              <!-- Loading -->
              <div v-if="loading" class="sessions-loading">
                <div class="spinner-border text-success spinner-border-sm" role="status"></div>
                <span>Loading sessions...</span>
              </div>

              <!-- Table -->
              <template v-else-if="sessions.length">
                <div class="table-responsive">
                  <table class="table sessions-table">
                    <thead>
                      <tr>
                        <th style="width:42px">S/N</th>
                        <th style="min-width:160px">Device</th>
                        <th>User Agent</th>
                        <th style="min-width:130px">IP Address</th>
                        <th style="min-width:150px">Last Login</th>
                        <th style="min-width:90px">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(session, idx) in sessions" :key="session.id">
                        <td class="row-num td-num" data-label="#">{{ (meta.current_page - 1) * meta.per_page + idx + 1 }}</td>
                        <td data-label="Device">
                          <div class="device-cell">
                            <span class="device-icon">
                              <i :class="deviceIcon(session.user_agent)"></i>
                            </span>
                            <div class="device-info">
                              <span class="device-name">{{ parseBrowser(session.user_agent) }}</span>
                              <span class="device-os">{{ parseOS(session.user_agent) }}</span>
                            </div>
                          </div>
                        </td>
                        <td data-label="User Agent">
                          <span
                            class="ua-text"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            :title="session.user_agent"
                          >{{ session.user_agent }}</span>
                        </td>
                        <td data-label="IP Address">
                          <span class="ip-text">{{ session.ip }}</span>
                        </td>
                        <td data-label="Last Login">
                          <span class="date-text">{{ formatDate(session.last_login_at) }}</span>
                        </td>
                        <td data-label="Status">
                          <span v-if="isCurrentSession(session.fingerprint)" class="badge-current">
                            <i class="fa fa-circle"></i> active
                          </span>
                          <span v-else class="badge-other">
                            <i class="fa fa-circle-o"></i> inactive
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Pagination -->
                <div class="pagination-wrap">
                  <div class="pagination-info">
                    Showing {{ meta.from }}&ndash;{{ meta.to }} of {{ meta.total }} entries
                  </div>
                  <div class="pagination-controls">
                    <select class="page-size-select" v-model="pageSize" @change="changePageSize">
                      <option :value="3">3</option>
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                      <option :value="100">100  </option>
                    </select>
                    <button class="page-btn" @click="listSessions(1)" :disabled="meta.current_page === 1" title="First">
                      <i class="fa fa-angle-double-left"></i>
                    </button>
                    <button class="page-btn" @click="listSessions(meta.current_page - 1)" :disabled="meta.current_page === 1" title="Previous">
                      <i class="fa fa-angle-left"></i>
                    </button>

                    <button
                      v-for="p in visiblePages"
                      :key="p"
                      class="page-btn"
                      :class="{ active: p === meta.current_page }"
                      @click="listSessions(p)"
                    >{{ p }}</button>

                    <button class="page-btn" @click="listSessions(meta.current_page + 1)" :disabled="meta.current_page === meta.last_page" title="Next">
                      <i class="fa fa-angle-right"></i>
                    </button>
                    <button class="page-btn" @click="listSessions(meta.last_page)" :disabled="meta.current_page === meta.last_page" title="Last">
                      <i class="fa fa-angle-double-right"></i>
                    </button>
                  </div>
                </div>
              </template>

              <!-- Empty -->
              <div v-else class="sessions-empty">
                <i class="fa fa-laptop sessions-empty-icon"></i>
                <p>No active sessions found.</p>
              </div>
            </div>
          </div>
        </div>

        <Copyright />
      </div>
    </div>
  </div>
</template>

<script>
import SideNav from "@/components/SideNav.vue";
import Copyright from "@/components/Copyright.vue";
import TopNav from "@/components/TopNav.vue";
import axios from "axios";

export default {
  name: "Sessions",
  components: { SideNav, TopNav, Copyright },

  data() {
    return {
      page_active: "settings",
      sessions: [],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0,
      },
      loading: false,
      currentFingerprint: null,
      pageSize: 10,
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const delta = 2;
      const cur = this.meta.current_page;
      const last = this.meta.last_page;
      for (let i = Math.max(1, cur - delta); i <= Math.min(last, cur + delta); i++) {
        pages.push(i);
      }
      return pages;
    },
  },

  mounted() {
    this.inactivityTime();
    this.listSessions(1);
  },

  beforeDestroy() {
    if (!window.bootstrap) return;
    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
      const instance = window.bootstrap.Tooltip.getInstance(el);
      if (instance) instance.dispose();
    });
  },

  methods: {
    listSessions(page = 1) {
      const token = this.$store.getters.headerstoken;
      if (!token) {
        this.$router.push("/");
        return;
      }
      this.loading = true;
      try {
        let data = this.loadNew({
          vendor_no: this.$store.getters.vendor_no,
          primary_legal_entity: this.$store.getters.entity,
         
        });
        axios({
          method: "get",
          url: this.$hostname + `/user-devices?page=${page}&per_page=${this.pageSize}`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Request-IV": data.iv,
          },
          params: { token: data.cipher },
        })
          .then((res) => {
            const payload = res.data;
            if (payload.status && payload.data) {
              const d = payload.data;
              this.sessions = d.data || [];
              this.meta = {
                current_page: d.current_page,
                last_page:    d.last_page,
                per_page:     d.per_page,
                total:        d.total,
                from:         d.from,
                to:           d.to,
              };
              if (!this.currentFingerprint && this.sessions.length) {
                this.currentFingerprint = this.sessions[0].fingerprint;
              }
              this.$nextTick(() => this.initTooltips());
            }
          })
          .catch((error) => {
            if (error.response.status === 401) {
              this.is401(error.response.status);
            }
          })
          .finally(() => {
            this.loading = false;
          });
      } catch (error) {
        console.error("Error fetching sessions:", error);
        this.loading = false;
      }
    },

    changePageSize() {
      this.listSessions(1);
    },

    initTooltips() {
      if (!window.bootstrap) return;
      document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        const existing = window.bootstrap.Tooltip.getInstance(el);
        if (existing) existing.dispose();
        new window.bootstrap.Tooltip(el);
      });
    },

    isCurrentSession(fingerprint) {
      return fingerprint === this.currentFingerprint;
    },

    parseBrowser(ua) {
      if (!ua) return "Unknown Browser";
      if (/Edg\//i.test(ua)) return "Microsoft Edge";
      if (/OPR\/|Opera/i.test(ua)) return "Opera";
      if (/Firefox\//i.test(ua)) return "Firefox";
      if (/Chrome\//i.test(ua)) return "Chrome";
      if (/Safari\//i.test(ua)) return "Safari";
      return "Unknown Browser";
    },

    parseOS(ua) {
      if (!ua) return "Unknown OS";
      if (/Windows NT 10/i.test(ua)) return "Windows 10/11";
      if (/Windows NT/i.test(ua)) return "Windows";
      if (/Android/i.test(ua)) {
        const m = ua.match(/Android ([0-9.]+)/i);
        return m ? `Android ${m[1]}` : "Android";
      }
      if (/iPhone|iPad/i.test(ua)) return "iOS";
      if (/Mac OS X/i.test(ua)) return "macOS";
      if (/Linux/i.test(ua)) return "Linux";
      return "Unknown OS";
    },

    deviceIcon(ua) {
      if (!ua) return "fa fa-desktop";
      return /Mobile|Android|iPhone|iPad/i.test(ua) ? "fa fa-mobile fa-lg" : "fa fa-desktop";
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr.replace(" ", "T"));
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.sessions-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  padding: 24px;
  margin-bottom: 24px;
}

.sessions-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.sessions-title {
  font-weight: 700;
  font-size: 15px;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.sessions-subtitle {
  font-size: 13px;
  color: #888;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.total-badge {
  background: #e6f4ee;
  color: #006e40;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

.btn-refresh {
  background: #f4f7f4;
  border: 1px solid #d4e8d4;
  color: #006e40;
  font-size: 13px;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-refresh:hover:not(:disabled) { background: #e2f0e2; }
.btn-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

.sessions-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 40px 0;
  justify-content: center;
  color: #888;
  font-size: 14px;
}

.sessions-table {
  margin: 0;
  font-size: 13px;
}

.sessions-table thead th {
  background: #f8faf8;
  color: #555;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 2px solid #e8f0e8;
  padding: 11px 14px;
  white-space: nowrap;
}

.sessions-table tbody td {
  padding: 13px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #f0f4f0;
}

.sessions-table tbody tr:last-child td { border-bottom: none; }
.sessions-table tbody tr:hover { background: #f9fbf9; }

.row-num {
  color: #bbb;
  font-size: 12px;
  font-weight: 600;
}

.device-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.device-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f0f7f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  flex-shrink: 0;
}

.device-info { display: flex; flex-direction: column; }

.device-name {
  font-weight: 600;
  color: #1a1a2e;
  font-size: 13px;
  line-height: 1.3;
  white-space: nowrap;
}

.device-os {
  font-size: 11px;
  color: #999;
  margin-top: 1px;
}

.ua-text {
  display: block;
  font-size: 12px;
  color: #555;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 380px;
  cursor: default;
}

.ip-text {
  font-family: monospace;
  font-size: 12px;
  color: #444;
  background: #f4f4f4;
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.date-text {
  font-size: 12px;
  color: #555;
  white-space: nowrap;
}

.badge-current {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #e6f4ee;
  color: #006e40;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.badge-current .fa-circle { font-size: 7px; }

.badge-other {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f5f5f5;
  color: #aaa;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 4px 4px;
  flex-wrap: wrap;
  gap: 10px;
  border-top: 1px solid #f0f4f0;
  margin-top: 4px;
}

.pagination-info {
  font-size: 13px;
  color: #888;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #e0eae0;
  border-radius: 6px;
  background: #fff;
  color: #444;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover:not(:disabled):not(.active) {
  background: #f0f7f0;
  border-color: #006e40;
  color: #006e40;
}

.page-btn.active {
  background: #006e40;
  border-color: #006e40;
  color: #fff;
  font-weight: 700;
  cursor: default;
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.sessions-empty {
  text-align: center;
  padding: 56px 0;
  color: #bbb;
}

.sessions-empty-icon {
  font-size: 44px;
  margin-bottom: 14px;
  display: block;
}

.sessions-empty p { font-size: 14px; margin: 0; }

.page-size-select {
  height: 32px;
  padding: 0 8px;
  border: 1px solid #e0eae0;
  border-radius: 6px;
  font-size: 13px;
  color: #555;
  background: #fff;
  cursor: pointer;
  margin-right: 6px;
}

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .sessions-card {
    padding: 16px;
  }

  .sessions-card-header {
    flex-direction: column;
    gap: 10px;
  }

  .btn-refresh {
    width: 100%;
    justify-content: center;
  }

  /* Hide the normal thead */
  .sessions-table thead {
    display: none;
  }

  /* Each row becomes a card */
  .sessions-table tbody tr {
    display: block;
    border: 1px solid #e8f0e8;
    border-radius: 8px;
    margin-bottom: 12px;
    padding: 4px 0;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }

  .sessions-table tbody tr:last-child td {
    border-bottom: 1px solid #f0f4f0;
  }

  .sessions-table tbody tr:last-child td:last-child {
    border-bottom: none;
  }

  .sessions-table tbody tr:hover {
    background: #fff;
  }

  /* Each cell becomes a labelled row */
  .sessions-table tbody td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 1px solid #f5f5f5;
    font-size: 13px;
  }

  /* Hide the # column on mobile */
  .sessions-table tbody td.td-num {
    display: none;
  }

  /* Label injected from data-label */
  .sessions-table tbody td::before {
    content: attr(data-label);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #999;
    white-space: nowrap;
    min-width: 80px;
    padding-top: 2px;
  }

  /* UA text: allow wrapping on mobile */
  .ua-text {
    max-width: 100%;
    white-space: normal;
    word-break: break-all;
  }

  /* Pagination stacks on mobile */
  .pagination-wrap {
    flex-direction: column;
    align-items: flex-start;
  }

  .pagination-controls {
    flex-wrap: wrap;
  }
}
</style>
